import dayjs from '@/helpers/dayjstz';
import faker from 'faker';
import last from 'lodash/last';
import { Response } from 'miragejs';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { StatusCodes as HTTP } from 'http-status-codes';
import {
    NEUTRAL_SENTIMENT,
    NEGATIVE_SENTIMENT,
    POSITIVE_SENTIMENT,
    NEUTRAL_SENTIMENT_LABEL,
    NEGATIVE_SENTIMENT_LABEL,
    POSITIVE_SENTIMENT_LABEL
} from '@/assets/common/sentiments';
import allCountries from '@/assets/common/countries';
import allLanguages from '@/assets/common/languages';
import socialMediaConfig from '@/assets/common/social-media-config';

function getRandomInt(max, min = 1) {
    return faker.datatype.number({ min, max });
}

function getRandomFloat(min, max, precision) {
    return faker.datatype.number({ min, max, precision });
}

function randomTrends() {
    const labels = [];

    for (let i = 1; i <= 30; i++) {
        labels.push(`Label ${i}`);
    }

    return labels.map(label => ({ label, value: getRandomInt(30) }));
}

function getValue(property, item) {
    const properties = property.split('.');

    let value = { ...item };

    for (const property of properties) {
        if (!value[property] && value[property] !== 0) {
            return null;
        }

        value = value[property];
    }

    return value;
}

function sortItems(property, order, items) {
    return [...items].sort((a, b) => {
        const aValue = getValue(property, a);
        const bValue = getValue(property, b);

        if (order === 'descend') {
            return aValue > bValue ? -1 : 1;
        }

        return aValue > bValue ? 1 : -1;
    });
}

function buildDemographicsData(range, items) {
    const labels = createUnixLabelsBasedOnTimeInUnix(range);

    return items.map(({ value }) => ({
        name: value,
        status_count: getRandomInt(20000, 1),
        percent: getRandomFloat(0, 100, 0.1),
        trend: {
            chart: {
                title: '...',
                data_type: 'amount',
                labels,
                graphs: [
                    {
                        type: '...',
                        label: '...',
                        data: labels.map(label => ({
                            x: label,
                            y: getRandomInt(15, 0)
                        }))
                    }
                ]
            }
        }
    }));
}

function timeInUnixToDates(timeInUnix) {
    const [startDate, endDate] = timeInUnix;

    const parsedStartDate = dayjs(startDate * 1000);

    const parsedEndDate = dayjs(endDate * 1000);

    return [parsedStartDate, parsedEndDate];
}

function createLabelsBasedOnTimeInUnixAndInterestPoints(timeInUnix, interestPoints) {
    if (!interestPoints || interestPoints < 0) {
        return [];
    }

    const labels = [];

    const [parsedStartDate, parsedEndDate] = timeInUnixToDates(timeInUnix);

    let currentDate = parsedStartDate;

    const differenceInSeconds = parsedEndDate.diff(currentDate) / 1000;

    const seconds = differenceInSeconds / interestPoints;

    labels.push(currentDate.format());

    while (currentDate.isSameOrBefore(parsedEndDate)) {
        currentDate = dayjs(cloneDeep(currentDate)).add(seconds, 'seconds');

        if (currentDate.isAfter(parsedEndDate)) {
            if (!parsedEndDate.isSame(dayjs(last(labels)))) {
                labels.push(parsedEndDate.format());
            }

            break;
        }

        labels.push(currentDate.format());
    }

    return labels;
}

function createUnixLabelsBasedOnTimeInUnix(timeInUnix) {
    const labels = [];

    const dayInSeconds = 24 * 60 * 60;

    const [parsedStartDate, parsedEndDate] = timeInUnixToDates(timeInUnix);

    let currentDate = parsedStartDate;

    const differenceInSeconds = parsedEndDate.diff(currentDate) / 1000;

    labels.push(currentDate.unix());

    const unit = differenceInSeconds <= dayInSeconds ? 'hour' : 'day';

    while (currentDate.isSameOrBefore(parsedEndDate)) {
        currentDate = dayjs(cloneDeep(currentDate)).add(1, unit);

        if (currentDate.isAfter(parsedEndDate)) {
            break;
        }

        labels.push(currentDate.unix());
    }

    return labels;
}

const keywords = [];

for (let i = 0; i < getRandomInt(25, 15); i++) {
    const multiplier = Math.random() > 0.5 ? 1 : -1;

    const keyword = {
        keyword: `Keyword ${i}`,
        mentions: {
            value: getRandomInt(2000),
            change: getRandomInt(200) * multiplier
        },
        trends: randomTrends(),
        sentiments: {
            positive: getRandomInt(500),
            negative: getRandomInt(500),
            neutral: getRandomInt(500)
        }
    };

    keywords.push(keyword);
}

export default server => {
    server.get('/v1/dashboard/layers', ({ layers }) => {
        const { models } = layers.all();

        return models;
    });

    server.post('/v1/dashboard/layers', ({ layers }, { requestBody }) => {
        const { name, type, color, brand } = JSON.parse(requestBody);

        const { attrs } = layers.create({
            name,
            type,
            brand,
            color
        });

        return attrs;
    });

    server.put('/v1/dashboard/layers/:id', ({ layers }, { params: { id }, requestBody }) => {
        const layer = layers.find(id);

        if (!layer) {
            return new Response(HTTP.NOT_FOUND);
        }

        const { name, type, color, brand } = JSON.parse(requestBody);

        const { attrs } = layer.update({
            name,
            type,
            brand,
            color
        });

        return attrs;
    });

    server.delete('/v1/dashboard/layers/:id', (schema, { params: { id } }) => {
        const item = schema.layers.find(id);

        if (item) {
            item.destroy();
        }

        return new Response(HTTP.NO_CONTENT);
    });

    // @DEPRECATED
    server.get(
        '/v1/dashboard/annotations',
        ({ annotations }, { queryParams: { timeInUnix, searchItemId, layerIds } }) => {
            if (!isArray(timeInUnix) || timeInUnix.length !== 2 || !Array.isArray(layerIds) || !layerIds?.length) {
                return new Response(HTTP.BAD_REQUEST);
            }

            const [startDate, endDate] = timeInUnix;

            const parsedStartDate = dayjs(startDate * 1000);
            const parsedEndDate = dayjs(endDate * 1000);

            const { models: foundAnnotations } = annotations.where(
                annotation =>
                    layerIds.includes(annotation.layerId) &&
                    annotation.searchItemId === searchItemId &&
                    dayjs(annotation.datetime).isSameOrAfter(parsedStartDate) &&
                    dayjs(annotation.datetime).isSameOrBefore(parsedEndDate)
            );

            return foundAnnotations;
        }
    );

    // @DEPRECATED
    server.post('/v1/dashboard/annotations', ({ annotations, users }, { requestBody }) => {
        const { name, userId, layerId, datetime, description, searchItemId } = JSON.parse(requestBody);

        const user = users.find(userId);

        if (!user) {
            return new Response(HTTP.NOT_FOUND);
        }

        const { id, firstName, lastName } = user;

        const { attrs } = annotations.create({
            name,
            layerId,
            datetime,
            description,
            searchItemId,
            createdAt: dayjs(),
            user: { id, fullName: `${firstName} ${lastName}` }
        });

        return attrs;
    });

    server.patch('/v1/dashboard/annotations/:id', (schema, { params: { id }, requestBody }) => {
        const annotation = schema.annotations.find(id);

        if (!annotation) {
            return new Response(HTTP.NOT_FOUND);
        }

        const { name, layerId, description, datetime } = JSON.parse(requestBody);

        const { attrs } = annotation.update({
            name,
            layerId,
            datetime,
            description
        });

        return attrs;
    });

    server.delete('/v1/dashboard/annotations/:id', (schema, { params: { id } }) => {
        const item = schema.annotations.find(id);

        if (item) {
            item.destroy();
        }

        return new Response(HTTP.NO_CONTENT);
    });

    server.get('/v1/dashboard/impact_score', () => {
        const multiplier = Math.random() > 0.5 ? 1 : -1;

        const data = {
            score: getRandomFloat(0, 10, 0.1),
            change: getRandomFloat(0, 15, 0.1) * multiplier,
            categories: [
                {
                    name: 'Reviews',
                    value: getRandomInt(500),
                    score: getRandomFloat(0, 10, 0.1),
                    change: getRandomFloat(0, 15, 0.1) * multiplier
                },
                {
                    name: 'Social',
                    value: getRandomInt(400),
                    score: getRandomFloat(0, 10, 0.1),
                    change: getRandomFloat(0, 15, 0.1) * multiplier
                },
                {
                    name: 'Ticketing',
                    value: getRandomInt(350),
                    score: getRandomFloat(0, 10, 0.1),
                    change: getRandomFloat(0, 15, 0.1) * multiplier
                }
            ]
        };

        return { data };
    });

    server.get('/v1/dashboard/reputation', (schema, { queryParams: { range } }) => {
        if (!isArray(range) || range.length !== 2) {
            return new Response(HTTP.NOT_FOUND);
        }

        const labels = createUnixLabelsBasedOnTimeInUnix(range);

        const trendChartData = labels.map(label => ({
            x: label,
            y: getRandomInt(15, 0.1)
        }));

        const data = {
            chart: {
                graphs: [
                    {
                        data: trendChartData
                    }
                ],
                labels
            }
        };

        return { data };
    });

    server.get('/v1/dashboard/sentiment', (schema, { queryParams: { range } }) => {
        if (!isArray(range) || range.length !== 2) {
            return new Response(HTTP.NOT_FOUND);
        }

        const labels = createUnixLabelsBasedOnTimeInUnix(range);

        const max = 700;

        const neutral = getRandomInt(max);

        let rest = max - neutral;

        const negative = getRandomInt(rest);

        rest = rest - negative;

        const positive = rest;

        const data = {
            chart: {
                graphs: [
                    {
                        label: POSITIVE_SENTIMENT_LABEL,
                        data: labels.map(label => ({
                            x: label,
                            y: getRandomInt(50)
                        }))
                    },
                    {
                        label: NEGATIVE_SENTIMENT_LABEL,
                        data: labels.map(label => ({
                            x: label,
                            y: getRandomInt(50)
                        }))
                    },
                    {
                        label: NEUTRAL_SENTIMENT_LABEL,
                        data: labels.map(label => ({
                            x: label,
                            y: getRandomInt(50)
                        }))
                    }
                ],
                labels
            },
            stats: [
                {
                    label: POSITIVE_SENTIMENT_LABEL,
                    value: ((positive / max) * 100).toFixed(2),
                    sum: positive
                },
                {
                    label: NEGATIVE_SENTIMENT_LABEL,
                    value: ((negative / max) * 100).toFixed(2),
                    sum: negative
                },
                {
                    label: NEUTRAL_SENTIMENT_LABEL,
                    value: ((neutral / max) * 100).toFixed(2),
                    sum: neutral
                }
            ]
        };

        return { data };
    });

    server.get('/v1/dashboard/compared_sentiment', (schema, { queryParams: { range } }) => {
        if (!isArray(range) || range.length !== 2) {
            return new Response(HTTP.NOT_FOUND);
        }

        const labels = createUnixLabelsBasedOnTimeInUnix(range);

        const generateGraphsByLabel = (label, amount) => {
            return new Array(amount).fill({
                label,
                data: labels.map(dateLabel => ({
                    x: dateLabel,
                    y: getRandomInt(50)
                }))
            });
        };

        const generateStatsByLabel = (label, amount) => {
            return new Array(amount).fill({
                label,
                value: getRandomInt(50),
                sum: getRandomInt(100)
            });
        };

        const data = {
            charts: {
                negative: {
                    graphs: generateGraphsByLabel(NEGATIVE_SENTIMENT_LABEL, 3),
                    labels
                },
                neutral: {
                    graphs: generateGraphsByLabel(NEUTRAL_SENTIMENT_LABEL, 3),
                    labels
                },
                positive: {
                    graphs: generateGraphsByLabel(POSITIVE_SENTIMENT_LABEL, 3),
                    labels
                }
            },
            stats: {
                negative: generateStatsByLabel(NEGATIVE_SENTIMENT_LABEL, 3),
                neutral: generateStatsByLabel(NEUTRAL_SENTIMENT_LABEL, 3),
                positive: generateStatsByLabel(POSITIVE_SENTIMENT_LABEL, 3)
            }
        };

        return { data };
    });

    server.get('/v1/dashboard/topics', (schema, { queryParams: { range, limit } }) => {
        const topics = [];

        const labels = createUnixLabelsBasedOnTimeInUnix(range);

        for (let i = 0; i < getRandomInt(limit, 15); i++) {
            const name = `Topic ${i}`;

            const topic = {
                score: getRandomFloat(0, 10, 0.1),
                sentiments: {
                    [NEUTRAL_SENTIMENT]: getRandomInt(500),
                    [POSITIVE_SENTIMENT]: getRandomInt(500),
                    [NEGATIVE_SENTIMENT]: getRandomInt(500)
                },
                statusCount: getRandomInt(4000),
                title: name,
                topicId: i,
                topicName: name,
                trend: {
                    chart: {
                        labels,
                        graphs: [
                            {
                                data: labels.map(label => ({
                                    x: label,
                                    y: getRandomInt(15, 0.1)
                                }))
                            }
                        ]
                    }
                }
            };

            topics.push(topic);
        }

        return {
            data: topics,
            metadata: {
                count: topics.length
            }
        };
    });

    server.get(
        '/v1/dashboard/trending-keywords',
        (schema, { queryParams: { sortBy, order, page = 1, perPage = 7 } }) => {
            const properties = {
                mentions: 'mentions.value',
                sentiments: 'sentiments.positive'
            };
            const property = properties[sortBy] || sortBy || 'mentions.value';

            const items = sortItems(property, order, keywords);

            page = parseInt(page);
            perPage = parseInt(perPage);

            const offset = (page - 1) * perPage;
            const limit = perPage + offset;

            return {
                items: items.slice(offset, limit),
                count: keywords.length
            };
        }
    );

    server.get('/v1/dashboard/reviews', (schema, { queryParams: { range } }) => {
        if (!isArray(range) || range.length !== 2) {
            return new Response(HTTP.NOT_FOUND);
        }

        const labels = createUnixLabelsBasedOnTimeInUnix(range);

        const totalReviews = getRandomInt(10, 100);

        let currentReviewsAmount = totalReviews;

        const data = [];

        const initialIndex = getRandomInt(5, 2);

        for (let index = initialIndex; index > 0; index--) {
            let statusCount = currentReviewsAmount > 0 ? getRandomInt(currentReviewsAmount) : 0;

            if (index === 1) {
                statusCount = currentReviewsAmount > 0 ? currentReviewsAmount : 0;
            }

            const rate = (statusCount / totalReviews) * 100;

            data.push({
                key: index,
                title: `Reviews rated ${index}`,
                statusCount,
                totalCount: totalReviews,
                percentages: {
                    [`rate-${index}`]: rate,
                    total: 100 - rate
                },
                trend: {
                    chart: {
                        dataType: 'amount',
                        labels,
                        graphs: [
                            {
                                data: labels.map(label => ({
                                    x: label,
                                    y: getRandomInt(3, 0)
                                }))
                            }
                        ]
                    }
                },
                dailyAvg: getRandomFloat(0, 5)
            });

            currentReviewsAmount -= statusCount;
        }

        return { data };
    });

    // @DEPRECATED
    server.get('/v1/dashboard/reviews-deprecated', (schema, { queryParams: { selectedSocialMediaFilters } }) => {
        const ratings = [
            {
                score: 5,
                reviews: getRandomInt(10000),
                trends: randomTrends()
            },
            {
                score: 4,
                reviews: 10000,
                trends: randomTrends()
            },
            {
                score: 3,
                reviews: 0,
                trends: randomTrends()
            },
            {
                score: 2,
                reviews: getRandomInt(500),
                trends: randomTrends()
            },
            {
                score: 1,
                reviews: getRandomInt(2000),
                trends: randomTrends()
            }
        ];

        const steamTypeAsString = socialMediaConfig.steam.id.toString();

        const isSteamActive = selectedSocialMediaFilters
            ? selectedSocialMediaFilters.some(socialMediaType => socialMediaType === steamTypeAsString)
            : false;

        const isOnlySteamActive = isEqual(selectedSocialMediaFilters, [steamTypeAsString]);

        return selectedSocialMediaFilters
            ? {
                  likesAndDislikesReviews: isSteamActive
                      ? [
                            {
                                type: 'likes',
                                score: 36600,
                                trends: randomTrends()
                            },
                            {
                                type: 'dislikes',
                                score: 30600,
                                trends: randomTrends()
                            }
                        ]
                      : null,
                  totalReviews: 11500,
                  avgReviewsPerDay: 158,
                  starsTotalAvgScore: isOnlySteamActive ? null : 4.5,
                  likesTotalAvgScore: 3.8,
                  totalReviewsCollected: 4321,
                  ratings: isOnlySteamActive ? [] : ratings
              }
            : {};
    });

    server.get(
        '/v1/dashboard/most-impactful-posts',
        (
            { feedItems },
            { queryParams: { user_id, selected_social_types, selected_sentiments, page = 1, perPage = 6 } }
        ) => {
            if (
                !Array.isArray(selected_social_types) ||
                !Array.isArray(selected_sentiments) ||
                !selected_social_types?.length ||
                !selected_sentiments?.length
            ) {
                return new Response(HTTP.BAD_REQUEST);
            }

            const { models: foundFeedItems } = feedItems.where(feedItem => feedItem.related_profiles.includes(user_id));

            page = parseInt(page);
            perPage = parseInt(perPage);

            const result = foundFeedItems.filter(
                feedItem =>
                    selected_social_types.includes(feedItem.social_type.toString()) &&
                    selected_sentiments.includes(feedItem.sentiment.toString())
            );

            const offset = (page - 1) * perPage;
            const limit = perPage + offset;

            return {
                items: result.slice(offset, limit),
                count: result.length
            };
        }
    );

    server.get('/v1/dashboard/demographics/countries', (schema, { queryParams: { limit = 25, range } }) => {
        if (!isArray(range) || range.length !== 2) {
            return new Response(HTTP.NOT_FOUND);
        }

        return {
            success: 'true',
            data: buildDemographicsData(range, allCountries.slice(0, limit))
        };
    });

    server.get('/v1/dashboard/demographics/languages', (schema, { queryParams: { limit = 25, range } }) => {
        if (!isArray(range) || range.length !== 2) {
            return new Response(HTTP.NOT_FOUND);
        }

        return {
            success: 'true',
            data: buildDemographicsData(range, allLanguages.slice(0, limit))
        };
    });

    server.get('/v1/dashboard/network-breakdown', (schema, { queryParams: { timeInUnix } }) => {
        if (!isArray(timeInUnix) || timeInUnix.length !== 2) {
            return new Response(HTTP.NOT_FOUND);
        }

        const labels = createLabelsBasedOnTimeInUnixAndInterestPoints(timeInUnix, getRandomInt(40, 10));

        const allNetworks = Object.values(socialMediaConfig).map(({ id }) => id);

        const networks = faker.random.arrayElements(allNetworks, getRandomInt(allNetworks.length));

        return labels.map(label => ({
            label,
            items: networks.map(network => ({
                network,
                positive: getRandomInt(300),
                neutral: getRandomInt(300),
                negative: getRandomInt(300)
            }))
        }));
    });

    server.get('/v1/dashboard/templates', ({ dashboardTemplates }) => {
        const { models } = dashboardTemplates.all();

        return models.map(({ attrs: { id, name } }) => ({ id, name }));
    });

    server.get('/v1/dashboard/brands', ({ brands }) => {
        const { models } = brands.all();

        return models;
    });

    server.get('v1/dashboard_selector', schema => {
        const { models: brandContexts } = schema.brandContexts.all();

        const categories = {};

        for (const brandContext of brandContexts) {
            const { category } = brandContext;

            categories[category] = {
                label: category
            };
        }

        const { models: teamContexts } = schema.teamContexts.all();

        const { models: accountContexts } = schema.accountContexts.all();

        return {
            data: { contexts: [...brandContexts, ...teamContexts, ...accountContexts], categories }
        };
    });

    server.get('/v1/dashboard/templates/:id', ({ dashboardTemplates }, { params: { id } }) => {
        const template = dashboardTemplates.find(id);

        if (!template) {
            return new Response(HTTP.NOT_FOUND);
        }

        const { attrs } = template;

        return attrs;
    });

    server.post('/v1/dashboard/templates', ({ dashboardTemplates }, { requestBody }) => {
        const { name, config, time, timeUnit } = JSON.parse(requestBody);

        const { attrs } = dashboardTemplates.create({
            name,
            time,
            config,
            timeUnit
        });

        return attrs;
    });

    server.patch('/v1/dashboard/templates/:id', ({ dashboardTemplates }, { params: { id }, requestBody }) => {
        const template = dashboardTemplates.find(id);

        if (!template) {
            return new Response(HTTP.NOT_FOUND);
        }

        const { name, config, time, timeUnit } = JSON.parse(requestBody);

        const { attrs } = template.update({
            name,
            time,
            config,
            timeUnit
        });

        return attrs;
    });
};
